import type {
  ClockPort,
  HijriCalendarPort,
  HijriDate,
  RamadanReviewWindow,
} from '../../application/ports';
import { IsoDateTime, LocalDate } from '../../domain';

interface DatePartsFormatter {
  formatToParts(date: Date): Intl.DateTimeFormatPart[];
}

type FormatterFactory = (locale: string, options: Intl.DateTimeFormatOptions) => DatePartsFormatter;

const formatterFactory: FormatterFactory = (locale, options) =>
  new Intl.DateTimeFormat(locale, options);

export class HijriCalendarError extends Error {
  constructor() {
    super('Umm al-Qura calendar conversion failed');
    this.name = 'HijriCalendarError';
  }
}

const numericPart = (
  parts: readonly Intl.DateTimeFormatPart[],
  type: Intl.DateTimeFormatPartTypes,
): number => {
  const value = parts.find((candidate) => candidate.type === type)?.value;
  if (value === undefined) throw new HijriCalendarError();
  return Number(value);
};

export class UmmAlQuraHijriAdapter implements HijriCalendarPort {
  constructor(
    private readonly clock: ClockPort,
    private readonly createFormatter: FormatterFactory = formatterFactory,
  ) {}

  toHijri(instant: IsoDateTime, timeZone: string): HijriDate {
    try {
      const parts = this.createFormatter('en-u-ca-islamic-umalqura-nu-latn', {
        calendar: 'islamic-umalqura',
        numberingSystem: 'latn',
        timeZone,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
      }).formatToParts(new Date(instant.value));
      return Object.freeze({
        year: numericPart(parts, 'year'),
        month: numericPart(parts, 'month'),
        day: numericPart(parts, 'day'),
      });
    } catch (error) {
      if (error instanceof HijriCalendarError) throw error;
      throw new HijriCalendarError();
    }
  }

  nextRamadanReviewWindow(timeZone: string): RamadanReviewWindow {
    const start = new Date(this.clock.now().value);
    start.setUTCHours(12, 0, 0, 0);
    let shaaban: { readonly date: LocalDate; readonly hijriYear: number } | undefined;

    for (let offset = 0; offset < 400; offset += 1) {
      const candidate = new Date(start.getTime() + offset * 86_400_000);
      const instant = IsoDateTime.create(candidate.toISOString());
      const hijri = this.toHijri(instant, timeZone);
      const localDate = this.toGregorianLocalDate(candidate, timeZone);
      if (shaaban === undefined && hijri.month === 8 && hijri.day === 1) {
        shaaban = Object.freeze({ date: localDate, hijriYear: hijri.year });
      }
      if (shaaban !== undefined && hijri.month === 9 && hijri.day === 1) {
        return Object.freeze({
          hijriYear: shaaban.hijriYear,
          shaabanStartsOn: shaaban.date,
          ramadanStartsOn: localDate,
        });
      }
    }
    throw new HijriCalendarError();
  }

  private toGregorianLocalDate(date: Date, timeZone: string): LocalDate {
    try {
      const parts = this.createFormatter('en-CA-u-ca-gregory-nu-latn', {
        calendar: 'gregory',
        numberingSystem: 'latn',
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).formatToParts(date);
      const year = String(numericPart(parts, 'year')).padStart(4, '0');
      const month = String(numericPart(parts, 'month')).padStart(2, '0');
      const day = String(numericPart(parts, 'day')).padStart(2, '0');
      return LocalDate.create(`${year}-${month}-${day}`);
    } catch (error) {
      if (error instanceof HijriCalendarError) throw error;
      throw new HijriCalendarError();
    }
  }
}
